/* eslint-disable no-undefined */
/* eslint-disable max-lines-per-function */
import { ArchievedDealsObject } from '../../params/redux/pipeline';
import DeleteDealModal from './modal/delete_deal';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TransferDealModal from './modal/transfer_deal';
import { AppDispatch, RootState } from '../../redux/store';
import { Box, Button, IconButton, Menu, MenuItem } from '@mui/material';
import { brand, red } from '../../theme/themePrimitives';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { getArchievedDealsThunk, restoreDealThunk } from '../../redux/thunks/deal';
import React, { useEffect, useState } from 'react';
import { settingArhievedList, settingSelectedDeal } from '../../redux/slices/deal';
import { useDispatch, useSelector } from 'react-redux';

interface DealsRow {
    id: number;
    title: string;
    company: string;
    status: string;
}

/**
 * @heading1: Archived Deals List Component
 * @paragraph: This component is used to display and manage the list of archived deals. It provides functionalities such as restoring deals, transferring deals, and deleting deals.
 */
const ArchievedList: React.FC = () => {
    /**
     * @heading3: Variables
     * @ol:
     * disaptch: {AppDisptach} - The Redux dispatch function to trigger actions.
     * showTransfer/anchorEl: React states to store the values for modal and popups.
     * archievedDeals/selectedPipeline: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const [showTransfer, setShowTransfer] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuDealId, setMenuDealId] = useState<number | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const { archievedDeals, loaders } = useSelector((state: RootState) => state.deal);
    const filteredDeals = archievedDeals.filter((deal) => deal.is_archieved === 1 && deal.is_deleted === 0);
    const open = Boolean(anchorEl);
    const rows: DealsRow[] = filteredDeals.map((deal: ArchievedDealsObject) => ({
        id: deal.id,
        title: deal.title,
        company: deal.company,
        status: deal.status
    })) || [];

    /**
     * @heading3: Functions
     * @heading4: handleClick
     * @paragraph: Opens the actions menu for a specific deal.
     * @param: {React.MouseEvent<HTMLButtonElement>} event - The click event on the menu icon.
     */
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
        setMenuDealId(id);
        setAnchorEl(event.currentTarget);
    };

    /**
     * @heading4: handleClose
     * @paragraph: Closes the actions menu.
     */
    const handleClose = () => {
        setAnchorEl(null);
        setMenuDealId(null);
    };

    /**
     * @heading4: dealTransfer
     * @paragraph: Opens the Transfer Deal modal.
     */
    // const dealTransfer = (deal: ArchievedDealsObject) => {
    //     dispatch(settingSelectedDeal(deal));
    //     setShowTransfer(true);
    //     handleClose();
    // };

    /**
     * @heading4: deleteDeal
     * @paragraph: Deletes a deal by updating its status to 'deleted'.
     * @param: {number} id - The ID of the deal to delete.
     */
    const deleteDeal = (dealId: number) => {
        const selectedDeal = filteredDeals.find((item) => item.id === dealId);
        dispatch(settingSelectedDeal(selectedDeal));
        setShowDeleteModal(true);
        handleClose();
    };

    /**
     * @heading4: restoreDeal
     * @paragraph: Restores a previously archived deal.
     * @param: {number} id - The ID of the deal to restore.
     */
    const restoreDeal = async (id: number) => {
        try {
            await dispatch(restoreDealThunk(id)).unwrap();
            handleClose();
        } catch (e) {
            //
        }
    };

    /**
    * @heading4: useEffect
    * @paragraph: Fetches the archived deals when the component mounts or the selected pipeline changes.
    */
    useEffect(() => {
        const fetchArchievedDeals = async () => {
            try {
                await dispatch(getArchievedDealsThunk()).unwrap();
            } catch (e) {
                //
            }
        };
        fetchArchievedDeals();
    }, []);

    const columns: GridColDef[] = [
        { field: 'title', headerName: 'DEAL NAME', flex: 1 },
        { field: 'company', headerName: 'COMPANY', flex: 1 },
        {
            field: 'status',
            headerName: 'STATUS',
            flex: 1,
            renderCell: (params: GridRenderCellParams<DealsRow, string>) => (
                <span style={{
                    color: 'red',
                    fontWeight: 'bold',
                    backgroundColor: red[50],
                    padding: '5px 10px',
                    borderRadius: '4px',
                    textTransform: 'uppercase'
                }}>
                    {params.value}
                </span>
            )
        },
        {
            field: 'actions',
            headerName: 'ACTIONS',
            sortable: false,
            renderCell: (params: GridRenderCellParams<DealsRow, string>) => (
                <>
                    <IconButton
                        aria-controls={open ? 'actions-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open}
                        onClick={(e) => handleClick(e, params.row.id)}
                    >
                        <MoreVertIcon />
                    </IconButton>
                    <Menu
                        id="actions-menu"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl && menuDealId && menuDealId === params.row.id)}
                        onClose={handleClose}
                    >
                        <MenuItem onClick={() => restoreDeal(menuDealId as number)}>Unarchived</MenuItem>
                        {/* <MenuItem onClick={() => dealTransfer(params.row)}>Transfer</MenuItem> */}
                        <MenuItem onClick={() => deleteDeal(menuDealId as number)}>Delete</MenuItem>
                    </Menu>
                </>
            )
        }
    ];

    return (
        <div style={{ padding: '20px' }}>
            {/* Archeived list header */}
            <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
                <Button onClick={() => dispatch(settingArhievedList(false))} style={{ marginBottom: '10px' }}>
                    &larr; Back to Pipelines
                </Button>
                <h2>Archived Deals</h2>
                <Box></Box>
            </Box>
            <Box height="70vh" width="100%">
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    loading={loaders.isLoading}
                    disableRowSelectionOnClick
                    pageSizeOptions={[25, 50, 75, 100]}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 25, page: 0 }
                        }
                    }}
                    sx={{
                        '& .MuiDataGrid-row--borderBottom': {
                            backgroundColor: `${brand[50]} !important`
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            fontWeight: 'bold'
                        },
                        '& .MuiDataGrid-cell': {
                            fontSize: '14px'
                        },
                        'width': '100%',
                        'overflowX': 'hidden'
                    }}
                />
            </Box>
            <DeleteDealModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} />
            {showTransfer && <TransferDealModal open={showTransfer} onClose={() => setShowTransfer(false)} />}
        </div>
    );
};

export default ArchievedList;
